import { useMemo, useState, useCallback } from "react";
import { BaseEditableTable } from "@/components/table/BaseEditableTable";
import type { EditableCellConfig, FilterConfig, CellUpdateFunction } from "@/components/table/types";
import type { TariffVersion } from "@/types/billing";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { AvatarDetailsButton } from "@/components/ui/avatar-details-button";
import { toast } from "sonner";
import { exportToCSV, type CSVExportColumn } from "@/utils/csvExport";
import type { RowSelectionState, PaginationState, ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { useLingui } from "@lingui/react/macro";
import { t, msg } from "@lingui/macro";
import { useTableConfig } from "@/hooks/settings/useTableConfig";
import { formatDateTime } from "@/lib/format";
import { useEnumOptions } from "@/hooks/settings/useEffectiveEnums";

interface TariffVersionsTableProps {
  data: TariffVersion[];
  onDetails?: (version: TariffVersion) => void;
  onCellUpdate?: CellUpdateFunction<TariffVersion>;
  enableSelection?: boolean;
  // Server-side pagination props
  serverSidePagination?: boolean;
  totalRows?: number;
  pagination?: PaginationState;
  onPaginationChange?: (updater: PaginationState | ((old: PaginationState) => PaginationState)) => void;
  isFetching?: boolean;
  // Server-side filter/sort/search callbacks
  onFilterChange?: (filters: ColumnFiltersState) => void;
  onSortingChange?: (sorting: SortingState) => void;
  onSearchChange?: (search: string) => void;
  // Current server-side filter/sort state (for syncing UI)
  serverFilters?: { status?: string };
  serverSorting?: { sortBy?: string; sortOrder?: 'asc' | 'desc' };
  toolbarRight?: React.ReactNode;
}


// CSV column definitions. Typed so `key` stays `keyof TariffVersion` instead of
// widening to `string` (which `exportToCSV` rejects).
const tariffVersionsCSVColumns: CSVExportColumn<TariffVersion>[] = [
  { key: "name", header: "Name" },
  { key: "description", header: "Description" },
  { key: "groupType", header: "Group Type" },
  { key: "status", header: "Status" },
  { key: "isDefault", header: "Is Default" },
  { key: "createdAt", header: "Created At" },
];

export function TariffVersionsTable({
  data,
  onDetails,
  onCellUpdate,
  enableSelection = true,
  serverSidePagination = false,
  totalRows,
  pagination,
  onPaginationChange,
  isFetching = false,
  onFilterChange,
  onSortingChange,
  onSearchChange,
  serverFilters: _serverFilters,
  serverSorting: _serverSorting, // Reserved for future use
  toolbarRight,
}: TariffVersionsTableProps) {
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const { i18n } = useLingui();

  // Get table configuration
  const { data: tableConfig } = useTableConfig("tariff-versions");

  // Get group type options from custom enums
  const { options: groupTypeOptions } = useEnumOptions('groups', 'type');

  // Build group type select options
  const groupTypeSelectOptions = useMemo(() => {
    if (groupTypeOptions.length > 0) {
      return groupTypeOptions;
    }
    // Fallback to STANDARD if no custom options
    return [{ value: "STANDARD", label: "Standard" }];
  }, [groupTypeOptions]);

  // Build status select options with translations
  const statusSelectOptions = useMemo(() => {
    return [
      { value: "DRAFT", label: i18n._(t({ id: "common.draft", message: "Draft" })) },
      { value: "ACTIVE", label: i18n._(t({ id: "common.active", message: "Active" })) },
      { value: "ARCHIVED", label: i18n._(t({ id: "common.archived", message: "Archived" })) },
    ];
  }, [i18n]);

  // Build is_default select options with translations
  const isDefaultSelectOptions = useMemo(() => {
    return [
      { value: "true", label: i18n._(t({ id: "common.yes", message: "Yes" })) },
      { value: "false", label: i18n._(t({ id: "common.no", message: "No" })) },
    ];
  }, [i18n]);

  // Define the columns for the tariff versions table
  const columns = useMemo<EditableCellConfig<TariffVersion>[]>(
    () => [
      // Avatar + Details button column
      {
        id: "avatar",
        header: "",
        accessorKey: "id" as keyof TariffVersion,
        editable: false,
        sortable: false,
        enableHiding: false,
        size: 120,
        cell: ({ row }) => (
          <AvatarDetailsButton
            firstName={row.original.name || "T"}
            lastName=""
            onClick={() => onDetails?.(row.original)}
          />
        ),
      },
      // Name column (not editable)
      {
        id: "name",
        header: i18n._(t({ id: "table.tariffVersions.columns.name" })),
        accessorKey: "name" as keyof TariffVersion,
        editable: false,
        sortable: true,
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.name}
          </span>
        ),
      },
      // Description column (not editable)
      {
        id: "description",
        header: i18n._(t({ id: "table.tariffVersions.columns.description" })),
        accessorKey: "description" as keyof TariffVersion,
        editable: false,
        sortable: true,
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm line-clamp-2">
            {row.original.description || "-"}
          </span>
        ),
      },
      // Group type column (editable dropdown)
      {
        id: "groupType",
        header: i18n._(t({ id: "table.tariffVersions.columns.groupType" })),
        accessorKey: "groupType" as keyof TariffVersion,
        editable: true,
        sortable: true,
        inputType: "select",
        selectOptions: groupTypeSelectOptions,
        cell: ({ row }) => {
          const groupType = row.original.groupType || row.original.group_type || "STANDARD";
          const option = groupTypeSelectOptions.find(opt => opt.value === groupType);
          return <span>{option?.label || groupType}</span>;
        },
      },
      // Status column (editable dropdown)
      {
        id: "status",
        header: i18n._(t({ id: "table.tariffVersions.columns.status" })),
        accessorKey: "status" as keyof TariffVersion,
        editable: true,
        sortable: true,
        inputType: "select",
        selectOptions: statusSelectOptions,
        filterFn: "arrIncludesSome",
        cell: ({ row }) => {
          const status = row.original.status || "DRAFT";
          const option = statusSelectOptions.find(opt => opt.value === status);
          return <span>{option?.label || status}</span>;
        },
      },
      // Is Default column (editable dropdown)
      {
        id: "isDefault",
        header: i18n._(t({ id: "table.tariffVersions.columns.isDefault" })),
        accessorKey: "isDefault" as keyof TariffVersion,
        editable: true,
        sortable: false,
        inputType: "select",
        selectOptions: isDefaultSelectOptions,
        // Convert boolean to string for the select
        getEditValue: (value: boolean | undefined) => value ? "true" : "false",
        cell: ({ row }) => {
          const isDefault = row.original.is_default || row.original.isDefault;
          const option = isDefaultSelectOptions.find(opt => opt.value === (isDefault ? "true" : "false"));
          return <span>{option?.label || (isDefault ? "Yes" : "No")}</span>;
        },
      },
      // Created at column
      {
        id: "createdAt",
        header: i18n._(t({ id: "table.tariffVersions.columns.createdAt" })),
        accessorKey: "createdAt" as keyof TariffVersion,
        editable: false,
        sortable: true,
        cell: ({ row }) => {
          const createdAt = row.original.createdAt || row.original.created_at;
          if (!createdAt) return "-";
          return formatDateTime(createdAt, i18n.locale) || "-";
        },
      },
    ],
    [i18n, onDetails, groupTypeSelectOptions, statusSelectOptions, isDefaultSelectOptions]
  );

  // Create localized CSV columns
  const localizedCSVColumns = useMemo(() => {
    return tariffVersionsCSVColumns.map((column) => ({
      ...column,
      header: (() => {
        switch (column.key) {
          case "name":
            return i18n._(t({ id: "table.tariffVersions.columns.name" }));
          case "description":
            return i18n._(t({ id: "table.tariffVersions.columns.description" }));
          case "groupType":
            return i18n._(t({ id: "table.tariffVersions.columns.groupType" }));
          case "status":
            return i18n._(t({ id: "table.tariffVersions.columns.status" }));
          case "isDefault":
            return i18n._(t({ id: "table.tariffVersions.columns.isDefault" }));
          case "createdAt":
            return i18n._(t({ id: "table.tariffVersions.columns.createdAt" }));
          default:
            return column.header || String(column.key);
        }
      })(),
    }));
  }, [i18n]);

  // Define filter configuration for each column
  const filterConfig: FilterConfig<TariffVersion> = useMemo(
    () => ({
      createdAt: "date",
      // Status filter removed - now handled by Current/All toggle
    }),
    []
  );

  // Handle CSV export
  const handleExportCSV = useCallback(
    (selectedRowIds: string[]) => {
      const versionsToExport =
        selectedRowIds.length > 0
          ? data.filter((version) => selectedRowIds.includes(version.id || ""))
          : data;

      if (versionsToExport.length === 0) {
        toast.error(
          i18n._(t({ id: "table.tariffVersions.actions.noVersionsToExport" }))
        );
        return;
      }

      const timestamp = new Date().toISOString().split("T")[0];
      const filename =
        selectedRowIds.length > 0
          ? `tariff-versions-selected-${timestamp}`
          : `tariff-versions-all-${timestamp}`;

      try {
        exportToCSV(versionsToExport, localizedCSVColumns, filename);
        toast.success(
          i18n._(
            msg({ id: "table.tariffVersions.actions.exportSuccess" }),
            { count: versionsToExport.length.toString() }
          )
        );
      } catch (error) {
        toast.error(i18n._(t({ id: "table.tariffVersions.actions.exportError" })));
        console.error("CSV export error:", error);
      }
    },
    [data, localizedCSVColumns, i18n]
  );

  // Render selection actions (export button)
  const renderSelectionActions = useCallback(
    (selectedRowIds: string[], clearSelection: () => void) => (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleExportCSV(selectedRowIds)}
          className="flex items-center space-x-2"
        >
          <Download className="h-4 w-4" />
          <span>
            {i18n._(
              msg({ id: "table.tariffVersions.actions.exportSelected" }),
              { count: selectedRowIds.length.toString() }
            )}
          </span>
        </Button>
        <Button variant="ghost" size="sm" onClick={clearSelection}>
          {i18n._(t({ id: "table.tariffVersions.actions.clearSelection" }))}
        </Button>
      </>
    ),
    [handleExportCSV, i18n]
  );

  // Prepare column visibility and order from table config
  const defaultColumnVisibility = useMemo(() => {
    if (!tableConfig?.visibleColumns) return undefined;

    // Captured after the guard: the narrowing is lost inside the callback below.
    const visibleColumns = tableConfig.visibleColumns;
    const visibility: Record<string, boolean> = {};
    columns.forEach((col) => {
      visibility[col.id || (col.accessorKey as string)] =
        visibleColumns.includes(col.accessorKey as string);
    });

    return visibility;
  }, [tableConfig?.visibleColumns, columns]);

  const defaultColumnOrder = useMemo(() => {
    return tableConfig?.columnOrder || undefined;
  }, [tableConfig?.columnOrder]);

  // Status filtering is now handled by the Current/All toggle at page level,
  // so no default column filters are needed for status
  const defaultColumnFilters: ColumnFiltersState = useMemo(() => {
    return [];
  }, []);

  // Handle cell update - convert isDefault string back to boolean
  const handleCellUpdate: CellUpdateFunction<TariffVersion> = useCallback(
    async (rowId, field, newValue) => {
      if (!onCellUpdate) {
        toast.error(i18n._(t({ id: "table.tariffVersions.readOnly" })));
        return;
      }

      // Convert isDefault from string to boolean
      let processedValue = newValue;
      if (field === "isDefault") {
        processedValue = newValue === "true";
      }

      await onCellUpdate(rowId, field, processedValue);
    },
    [onCellUpdate, i18n]
  );

  return (
    <BaseEditableTable<TariffVersion>
      data={data}
      columns={columns}
      onCellUpdate={handleCellUpdate}
      defaultSortField={
        (tableConfig?.defaultSortColumn as keyof TariffVersion) || "createdAt"
      }
      defaultSortOrder={tableConfig?.defaultSortDesc ? "desc" : "asc"}
      defaultPageSize={serverSidePagination ? (pagination?.pageSize || 50) : 10}
      enableSelection={enableSelection}
      selectedRows={selectedRows}
      onSelectionChange={setSelectedRows}
      renderSelectionActions={renderSelectionActions}
      enableSearch={true}
      searchPlaceholder={i18n._(
        t({ id: "table.tariffVersions.search.placeholder" })
      )}
      onSearchChange={onSearchChange}
      enableFilters={true}
      filterConfig={filterConfig}
      onFilterChange={onFilterChange}
      onSortingChange={onSortingChange}
      enableColumnVisibility={true}
      enableColumnOrdering={true}
      tableId="tariff-versions-table"
      defaultColumnVisibility={defaultColumnVisibility}
      defaultColumnOrder={defaultColumnOrder}
      defaultColumnFilters={defaultColumnFilters}
      toolbarRight={toolbarRight}
      // Server-side pagination props
      serverSidePagination={serverSidePagination}
      totalRows={totalRows}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      isFetching={isFetching}
    />
  );
}
